import { requestApi } from "../api/client.js";

export type FollowUpStatus = "closed" | "in_progress" | "open";
export type FollowUpSeverity = "critical" | "major" | "minor";
export type FollowUpDecision =
  | "accepted_variance"
  | "dismissed"
  | "edited_record"
  | "escalated"
  | "resolved";

export interface FollowUp {
  assigned_staff_profile_id: string | null;
  created_at: string;
  due_at: string | null;
  id: string;
  location_id: string;
  manager_decision: FollowUpDecision | null;
  manager_note: string | null;
  record_id: string | null;
  severity: FollowUpSeverity;
  staff_responded_at: string | null;
  staff_response: string | null;
  status: FollowUpStatus;
  suggested_action: string | null;
  task_occurrence_id: string | null;
  trigger_kind:
    | "incident"
    | "manual"
    | "missed_task"
    | "rejected_delivery"
    | "rule_fail"
    | "rule_warn";
}

export interface FoodDisposition {
  batch_lot: string | null;
  decided_at: string;
  decided_by: string;
  decision: "discard" | "quarantine" | "release" | "rework";
  follow_up_id: string;
  id: string;
  note: string | null;
  product: string;
  quantity: string | null;
}

interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export function listFollowUps(
  locationId: string,
  filters: { severity?: FollowUpSeverity; status?: FollowUpStatus },
  cursor?: string,
): Promise<Page<FollowUp>> {
  const query = new URLSearchParams({ limit: "50", location_id: locationId });
  if (filters.status) query.set("status", filters.status);
  if (filters.severity) query.set("severity", filters.severity);
  if (cursor) query.set("cursor", cursor);
  return requestApi<Page<FollowUp>>(`/follow-ups?${query.toString()}`);
}

export function respondToFollowUp(
  id: string,
  response: string,
): Promise<FollowUp> {
  return requestApi<FollowUp>(`/follow-ups/${id}/respond`, {
    body: JSON.stringify({ response }),
    method: "POST",
  });
}

export function resolveFollowUp(
  id: string,
  input: { decision: FollowUpDecision; note?: string | null },
): Promise<FollowUp> {
  return requestApi<FollowUp>(`/follow-ups/${id}/resolve`, {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export function createDisposition(
  id: string,
  input: {
    batch_lot?: string | null;
    decision: FoodDisposition["decision"];
    note?: string | null;
    product: string;
    quantity?: string | null;
  },
): Promise<FoodDisposition> {
  return requestApi<FoodDisposition>(`/follow-ups/${id}/dispositions`, {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export function severityTone(
  severity: FollowUpSeverity,
): "attention" | "danger" | "neutral" {
  return severity === "critical"
    ? "danger"
    : severity === "major"
      ? "attention"
      : "neutral";
}

export function triggerLabel(trigger: FollowUp["trigger_kind"]): string {
  const labels: Record<FollowUp["trigger_kind"], string> = {
    incident: "Incident",
    manual: "Manager follow-up",
    missed_task: "Missed task",
    rejected_delivery: "Rejected delivery",
    rule_fail: "Failed check",
    rule_warn: "Check outside target",
  };
  return labels[trigger];
}
